import * as React from 'react';
import { createContext, ReactNode, useContext, useEffect, useRef } from 'react';
import { StateCreator, StoreApi, UseBoundStore } from 'zustand';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn, useStoreWithEqualityFn } from 'zustand/traditional';

export type ZustandStore<TState extends object> = UseBoundStore<StoreApi<TState>>;

export type ZustandReactContext<
    TState extends object
> = React.Context<ZustandStore<TState> | null>;

export type ZustandContextInitializer<TState extends object> = StateCreator<TState>;

/**
 * Used to create a React context for a zustand store.
 *
 * @example
 * export type BearState = {
 *   bears: number;
 *   incBears: () => void;
 * };
 *
 * const BearZustandContext = createZustandContext<BearState>('Awesome bear store');
 *
 */
export function createZustandContext<TState extends object>(displayName?: string) {
    const ctx = createContext<ZustandStore<TState> | null>(null);
    ctx.displayName = displayName;
    return ctx;
}

export type ZustandContextProviderProps<TState extends object> = {
    ctx: ZustandReactContext<TState>;
    initializer: ZustandContextInitializer<TState>;
    children: ReactNode;
};

/**
 * Component used to make a zustand store available in a React context.
 *
 * @example
 * export type BearState = {
 *   bears: number;
 *   addBear: () => void;
 * };
 *
 * const BearZustandContext = createZustandContext<BearState>();
 *
 * export type BearProviderProps = { children: ReactNode };
 *
 * export function BearProvider({ children }: BearProviderProps) {
 *   const initializer = useMemo<ZustandContextInitializer<BearState>>(
 *     () => (set) => ({
 *       bears: 0,
 *       addBear: () => set((state) => ({ bears: state.bears + 1 }))
 *     }),
 *     []
 *   );
 *
 *   return (
 *     <ZustandContextProvider<BearState>>
 *       ctx={BearZustandContext}
 *       initializer={initializer}
 *     >
 *       {children}
 *     </ZustandContextProvider>
 *   );
 * }
 */
export function ZustandContextProvider<TState extends object>({
    ctx,
    initializer,
    children,
}: ZustandContextProviderProps<TState>) {
    const prevInitializer = useRef<ZustandContextInitializer<TState>>();
    const store = useRef<ZustandStore<TState>>();
    const { Provider } = ctx;

    // Reinitialize store if not initialized or the initializer function changes
    if (prevInitializer.current !== initializer || store.current === undefined) {
        // Initialize store
        store.current = createWithEqualityFn(initializer, (_a, _b) => {
            throw new Error('Provide an explicit equiality function');
        });
        prevInitializer.current = initializer;
    }

    // Clean up store when component is unmounted
    useEffect(
        () => () => {
            store.current = undefined;
            prevInitializer.current = undefined;
        },
        []
    );

    return <Provider value={store.current}>{children}</Provider>;
}

/**
 * Returns the zustand store object/hook associated with a React context.
 *
 * @param ctx the zustand react context
 * @returns the zustand store object/hook
 *
 * @example
 * const useBearStore = useZustandStore(BearContext);
 *
 * useEffect(() => {
 *   const unsubscribe = useBearStore.subscribe((state) => {
 *     console.log('state change:', state);
 *   });
 *
 *   return unsubscribe;
 * }, [useBearStore]);
 */
export function useZustandStore<TState extends object>(
    ctx: ZustandReactContext<TState>
): ZustandStore<TState> {
    const reactCtx = useContext(ctx);

    if (reactCtx === null) {
        throw new Error('Missing context');
    }

    return reactCtx;
}

/**
 * Returns a zustand store hook for plucking store state.
 *
 * @param ctx the zustand react context
 * @returns the hook for accessing state value
 *
 * @example
 * const useBearState = getZustandStoreHook(BearContext);
 *
 * const BearStats = () => {
 *
 *   const wildBears = useBearState((state) => state.wildBears)
 *
 *   return (
 *     <div>Bears in the wild: {wildBears}</div>
 *   )
 * }
 */
export function getZustandStoreHook<TState extends object>(
    ctx: ZustandReactContext<TState>
) {
    function useStoreValueHook<TResult>(
        selector: (state: TState) => TResult,
        equals?: (a: TResult, b: TResult) => boolean
    ) {
        return useStoreWithEqualityFn(
            useZustandStore(ctx),
            selector,
            equals ?? Object.is
        );
    }

    return useStoreValueHook;
}

/**
 * Returns a zustand store hook for plucking multiple properties from the store
 * state.  It will use zustand's shallow comparison for for change detection.
 *
 * @param ctx the zustand react context
 * @returns the hook for accessing state values
 *
 * @example
 * const useBearState = getZustandStoreShallowHook(BearContext);
 *
 * // Plucking state via object
 * const BearStats = () => {
 *
 *   // object
 *   const { wildBears, captiveBears } = useBearState((state) => ({
 *     wildBears: state.wildBears,
 *     captiveBears: state.captiveBears,
 *   }))
 *
 *   return (
 *     <div>Bears in the wild: {wildBears}</div>
 *     <div>Bears in captivity: {captiveBears}</div>
 *   )
 * }
 *
 * // Plucking state via array
 * const BearStats = () => {
 *
 *   const [wildBears, captiveBears] = useBearState((state) => [
 *     state.wildBears,
 *     state.captiveBears,
 *   ])
 *
 *   return (
 *     <div>Bears in the wild: {wildBears}</div>
 *     <div>Bears in captivity: {captiveBears}</div>
 *   )
 * }
 *
 */
export function getZustandStoreShallowHook<TState extends Record<string, unknown>>(
    ctx: ZustandReactContext<TState>
) {
    function useStoreValueHook<TResult extends Record<string, unknown> | unknown[]>(
        selector: (state: TState) => TResult,
        equals: (a: TResult, b: TResult) => boolean = shallow
    ) {
        return useStoreWithEqualityFn(useZustandStore(ctx), selector, equals);
    }

    return useStoreValueHook;
}

export type KeyedStoreGetter<
    TKey extends string,
    TState extends Record<string, unknown>
> = {
    (moduleKey: TKey | undefined): ZustandStore<TState>;
};
export type KeyedStore<TKey extends string, TState extends Record<string, unknown>> = {
    moduleKey: TKey | undefined;
    setModuleKey: (moduleKey: TKey | undefined) => void;
    current: () => ZustandStore<TState>;
    stores: Map<TKey | 'nokey', ZustandStore<TState>>;
    get: KeyedStoreGetter<TKey, TState>;
};

export function getKeyedZustandStoreChangeModuleHook<
    TState extends Record<string, unknown>,
    TKey extends string
>(ctx: ZustandReactContext<KeyedStore<TKey, TState>>) {
    function useStoreChangeModuleHook() {
        return {
            setModuleKey: useZustandStore(ctx).getState().setModuleKey,
        };
    }

    return useStoreChangeModuleHook;
}

export function getKeyedZustandStoreShallowHook<
    TState extends Record<string, unknown>,
    TKey extends string
>(ctx: ZustandReactContext<KeyedStore<TKey, TState>>) {
    function useStoreValueHook<TResult extends Record<string, unknown> | unknown[]>(
        selector: (state: TState) => TResult,
        equals: (a: TResult, b: TResult) => boolean = shallow
    ) {
        const currentState = useZustandStore(ctx).getState();
        return useStoreWithEqualityFn(
            currentState.get(currentState.moduleKey),
            selector,
            equals
        );
    }

    return useStoreValueHook;
}

/**
 * Used to wrap a component in a Zustand context.
 *
 * @param ComponentToWrap the component to wrap
 * @param ctx the zustand react context
 * @param initializer the zustand state initializer function
 * @returns a wrapped component
 *
 * @example
 *
 * const BearContext = createZustandContext<BearState>();
 *
 * const initializeBearState = useMemo<ZustandContextInitializer<BearState>>((set) => ({
 *   bears: 0,
 *   addBear: () => set((state) => ({ bears: state.bears + 1 }))
 * });
 *
 * const BearInfo = (props: BearInfoProps) => {
 *   // bear store available
 *   const useBearStore = useZustandStore(BearContext);
 *
 *   // [content here]
 * }
 *
 * export default wrapWithZustandContext(
 *   BearInfo,
 *   BearContext,
 *   initializeBearState
 * );
 */
export function wrapWithZustandContext<TState extends object, TProps extends object>(
    ComponentToWrap: React.ComponentType<TProps>,
    ctx: ZustandReactContext<TState>,
    initializer: ZustandContextInitializer<TState>
): React.ComponentType<TProps> {
    return (props: TProps) => (
        <ZustandContextProvider ctx={ctx} initializer={initializer}>
            <ComponentToWrap {...props} />
        </ZustandContextProvider>
    );
}
